import {
  Autocomplete,
  Box,
  FormControl,
  FormLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { RestoreAccount, AzureStorageAccount } from '@repo/api-gw-sdk';
import { useEffect, type Dispatch, type SetStateAction } from 'react';

import { TransactionalTextField } from '@/components/shared/transactionalTextField';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';
import { PreferencesKey } from '@/data/dal/preferencesKey';

import type { RestoreGenericStorageProps } from './bucketSelection';

type StorageAccountSelectionStepProps<T> = StepProps<
  T & {
    restoreAccountId?: string;
  }
>;

export const StorageAccountSelectionStep = <
  T extends RestoreGenericStorageProps,
>(
  props: StorageAccountSelectionStepProps<T> & {
    allowUsingSourceContainer: boolean;
  }
) => {
  const { setWizardState: setStorageAccountSelectionState } = props;
  const dal = useDAL();
  const { data: pref } = dal.preferences.useUserPref(
    PreferencesKey.RestoreStorageAccount
  );

  useEffect(() => {
    if (pref?.value?.accountName) {
      setStorageAccountSelectionState((state) => ({
        ...state,
        StorageAccount: {
          ...state.StorageAccount,
          AccountName: pref.value!.accountName,
          Container: pref.value!.container,
          ResourceGroup: pref.value!.resourceGroup,
        },
      }));
    }
  }, [pref, setStorageAccountSelectionState]);

  return (
    <StorageAccountSelection
      {...props.wizardState}
      allowUsingSourceContainer={props.allowUsingSourceContainer}
      setStorageAccountSelectionState={setStorageAccountSelectionState}
    />
  );
};

interface StorageAccountProps<T> extends RestoreGenericStorageProps {
  restoreAccount?: RestoreAccount;
  allowUsingSourceContainer: boolean;
  restrictToRegion?: string;
  setStorageAccountSelectionState: Dispatch<
    SetStateAction<
      T & {
        restoreAccount?: RestoreAccount;
      }
    >
  >;
}

export const StorageAccountSelection = <T extends RestoreGenericStorageProps>(
  props: StorageAccountProps<T>
) => {
  const {
    restoreAccount,
    prefix,
    StorageAccount,
    setStorageAccountSelectionState,
    restrictToRegion,
  } = props;
  const dal = useDAL();
  const { data: storageAccountsResponse } = dal.restore.storageAccounts.useList(
    restoreAccount?.id ?? ''
  );
  const storageAccounts = (
    storageAccountsResponse?.storageAccounts ?? []
  ).filter((x) => !restrictToRegion || x.location === restrictToRegion);

  const selectedStorageAccount = storageAccounts.find(
    (x) => x.name === StorageAccount.AccountName
  );
  const { data: containersResponse } =
    dal.restore.storageAccounts.useListContainers(
      restoreAccount?.id ?? '',
      selectedStorageAccount?.name ?? '',
      selectedStorageAccount?.resourceGroup ?? ''
    );

  const list = (containersResponse?.containers ?? []).map((name) => ({
    name,
    value: name,
  }));

  const containersList = props.allowUsingSourceContainer
    ? [{ name: 'Use source containers', value: '' }, ...list]
    : list;

  const setRestoreStorageAccountName = (value?: AzureStorageAccount) => {
    setStorageAccountSelectionState((state) => ({
      ...state,
      StorageAccount: {
        ...state.StorageAccount,
        AccountName: value?.name,
        ResourceGroup: value?.resourceGroup,
        Container: '',
      },
    }));
  };

  const setContainer = (value: string) => {
    setStorageAccountSelectionState((state) => ({
      ...state,
      StorageAccount: { ...state.StorageAccount, Container: value },
    }));
  };

  const setPrefix = (value: string) => {
    setStorageAccountSelectionState((state) => ({
      ...state,
      prefix: value,
    }));
  };

  return (
    <Box className='mx-[40px] my-[24px]'>
      <Stack direction='row' gap='24px' className='mt-[20px] mb-[24px]'>
        <FormControl size='small' className='flex-1'>
          <FormLabel>Storage Account*</FormLabel>
          <Autocomplete
            clearOnBlur
            size='small'
            value={
              storageAccounts.find(
                (x) => x.name === StorageAccount.AccountName
              ) || null
            }
            renderOption={(props, option) => (
              <li {...props} key={option.name}>
                <StorageAccountRegionItem {...option} />
              </li>
            )}
            onChange={(event, newValue) =>
              setRestoreStorageAccountName(newValue || undefined)
            }
            getOptionLabel={(option) => option.name}
            renderInput={(params) => {
              return (
                <TextField
                  {...params}
                  sx={{
                    '& .MuiInputBase-input': {
                      height: '40px',
                    },
                  }}
                  placeholder='Select'
                  label=''
                  InputLabelProps={{ shrink: true }}
                />
              );
            }}
            options={storageAccounts}
          />
        </FormControl>
        <FormControl size='small' className='flex-1'>
          <FormLabel>Container*</FormLabel>
          <Autocomplete
            clearOnBlur
            disabled={!StorageAccount.AccountName}
            size='small'
            value={
              containersList.find(
                (x) => x.value === StorageAccount.Container
              ) || null
            }
            onChange={(event, newValue) => setContainer(newValue?.value || '')}
            getOptionLabel={(option) => option.name}
            renderInput={(params) => {
              return (
                <TextField
                  {...params}
                  sx={{
                    '& .MuiInputBase-input': {
                      height: '40px',
                    },
                  }}
                  placeholder='Select'
                  label=''
                  InputLabelProps={{ shrink: true }}
                />
              );
            }}
            options={containersList}
          />
        </FormControl>
      </Stack>
      <Stack direction='row' gap='24px' className='mt-[20px] mb-[24px]'>
        <StorageAccountPrefix prefix={prefix} onChange={setPrefix} />
      </Stack>
    </Box>
  );
};

const StorageAccountRegionItem = (props: {
  name: string;
  location: string;
}) => {
  return (
    <Stack direction={'column'} className='py-[4px]'>
      <Typography>{props.name}</Typography>
      <Typography variant='body2' className='mt-[4px]'>
        {props.location}
      </Typography>
    </Stack>
  );
};

const StorageAccountPrefix = (props: {
  prefix?: string;
  onChange: (value: string) => void;
}) => {
  return (
    <FormControl size='small' className='flex-1'>
      <FormLabel>Prefix (optional)</FormLabel>
      <Stack height='100%' direction='column' justifyContent='space-between'>
        <TransactionalTextField
          initValue={props.prefix || ''}
          onChange={props.onChange}
        />
        <Typography paddingTop='8px' variant='body2'>
          Example: project/wordFiles/
        </Typography>
      </Stack>
    </FormControl>
  );
};
